import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { useGameStore } from '../../store/gameStore';
import { gameAPI } from '../../services/gameAPI';
import { Users, User, Skull, AlertCircle } from 'lucide-react';
import { SuspectDetailModal } from './SuspectDetailModal';
import { isInterviewAction, MAX_INTERROGATION_ROUNDS } from '../../utils/actionHelpers';
import { TutorialBanner } from '../ui/TutorialBanner';

export const SuspectWall = ({ open, onClose, onStartInterrogation, tutorialInfo }) => {
  const {
    suspects,
    discovered,
    selectedTheme,
    caseData,
    messageHistory,
    sessionId,
    interrogationDialogues,
    gameStatus,
  } = useGameStore();
  // A won/lost case stays on the board, but its suspects can no longer be
  // questioned; withholding onInterview hides the Interview buttons (PG-21).
  const isGameOver = gameStatus === 'won' || gameStatus === 'lost';

  const [selectedSuspect, setSelectedSuspect] = useState(null);
  const [alreadyInterrogatedPopup, setAlreadyInterrogatedPopup] = useState(false);
  const [popupSuspectName, setPopupSuspectName] = useState('');

  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  }[selectedTheme || 'noir'];

  // Get discovered suspects only (with defensive checks)
  const discoveredSuspects = (suspects || []).filter(s => discovered?.suspects?.includes(s.id));

  // Check if suspect has been interviewed or interrogated
  const hasBeenInterviewed = (suspectId) => {
    // Check interrogation dialogues first
    if (interrogationDialogues && interrogationDialogues[suspectId] && interrogationDialogues[suspectId].length > 0) {
      return true;
    }
    // Fall back to message history
    return messageHistory.some(
      msg => isInterviewAction(msg.action) && msg.target === suspectId && msg.narrative
    );
  };

  // Get interview/interrogation history for a suspect (deduplicated)
  const getInterviewHistory = (suspectId) => {
    const history = [];

    // Add interrogation dialogues from dedicated storage (preferred source)
    if (interrogationDialogues && interrogationDialogues[suspectId]) {
      history.push(...interrogationDialogues[suspectId]);
    }

    // Also include any interview messages from message history (for backwards compatibility)
    const seen = new Set();
    const messageInterviews = messageHistory.filter(msg => {
      if (!isInterviewAction(msg.action) || msg.target !== suspectId || !msg.narrative) {
        return false;
      }
      // Skip placeholder text
      if (msg.narrative === 'You continue your investigation...') {
        return false;
      }
      // Deduplicate by first 100 chars of narrative
      const key = msg.narrative.substring(0, 100);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    // Add message-based interviews if we don't have interrogation dialogues
    if (history.length === 0) {
      history.push(...messageInterviews);
    }

    return history;
  };

  const handleInterviewClick = async (suspectId) => {
    const suspect = (suspects || []).find(s => s.id === suspectId);
    if (!suspect) return;

    try {
      // Check interrogation status from API
      const status = await gameAPI.getInterrogationStatus(sessionId, suspectId);
      
      if (status.rounds_completed >= MAX_INTERROGATION_ROUNDS) {
        // Already fully interrogated - show popup
        setPopupSuspectName(suspect.name);
        setAlreadyInterrogatedPopup(true);
      } else {
        // Can still interrogate - close modals and open interrogation
        setSelectedSuspect(null);
        onClose();
        if (onStartInterrogation) {
          onStartInterrogation(suspect, status.rounds_completed || 0);
        }
      }
    } catch (err) {
      console.error('Error checking interrogation status:', err);
      // If error, try to open interrogation anyway
      setSelectedSuspect(null);
      onClose();
      if (onStartInterrogation) {
        onStartInterrogation(suspect, 0);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl lg:max-w-6xl h-[80vh] lg:h-[85vh] bg-gradient-to-br from-slate-900 to-slate-800 border-2" style={{ borderColor: themeAccent }}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white flex items-center gap-2">
              <Users className="w-6 h-6" style={{ color: themeAccent }} />
              Suspect Wall
            </DialogTitle>
          </DialogHeader>

          {/* Tutorial info banner */}
          {tutorialInfo && <TutorialBanner title={tutorialInfo.title} content={tutorialInfo.content} />}

          <ScrollArea className="flex-1 pr-4">
            {/* Victim Section */}
            {caseData?.victim && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-400 uppercase mb-3">Victim</h3>
                <div className="flex justify-center">
                  <div
                    className="relative p-4 rounded-lg bg-red-950/50 border-2 border-red-500/50 w-40 text-center"
                  >
                    <div className="relative">
                      {caseData.victim.portrait ? (
                        <img
                          src={caseData.victim.portrait}
                          alt={caseData.victim.name}
                          className="w-24 h-24 rounded-full object-cover mx-auto border-2 border-red-500"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-red-900 flex items-center justify-center mx-auto border-2 border-red-500">
                          <Skull className="w-10 h-10 text-red-400" />
                        </div>
                      )}
                      <div className="absolute -top-1 -right-1 bg-red-500 rounded-full p-1">
                        <Skull className="w-3 h-3 text-white" />
                      </div>
                    </div>
                    <p className="text-white font-bold mt-2 text-sm">{caseData.victim.name}</p>
                    <p className="text-red-400 text-xs">DECEASED</p>
                    <p className="text-gray-400 text-xs mt-1">{caseData.victim.occupation}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Suspects Grid */}
            <div>
              <h3 className="text-sm font-semibold text-gray-400 uppercase mb-3">
                Persons of Interest ({discoveredSuspects.length})
              </h3>

              {discoveredSuspects.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  No suspects discovered yet. Investigate the crime scene and interview witnesses.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {discoveredSuspects.map((suspect) => {
                    const interviewed = hasBeenInterviewed(suspect.id);
                    return (
                      <div
                        key={suspect.id}
                        onClick={() => setSelectedSuspect(suspect)}
                        className={`
                          relative p-4 rounded-lg cursor-pointer transition-all duration-200
                          hover:scale-105 hover:shadow-lg
                          ${interviewed
                            ? 'bg-slate-800/80 border-2'
                            : 'bg-slate-800/40 border border-dashed border-white/20'
                          }
                        `}
                        style={{
                          borderColor: interviewed ? `${themeAccent}60` : undefined
                        }}
                      >
                        {/* Portrait */}
                        <div className="relative">
                          {suspect.portrait ? (
                            <img
                              src={suspect.portrait}
                              alt={suspect.name}
                              className={`
                                w-20 h-20 rounded-full object-cover mx-auto border-2
                                ${interviewed ? '' : 'grayscale opacity-70'}
                              `}
                              style={{
                                borderColor: interviewed ? themeAccent : '#4a5568'
                              }}
                            />
                          ) : (
                            <div
                              className="w-20 h-20 rounded-full bg-slate-700 flex items-center justify-center mx-auto border-2"
                              style={{
                                borderColor: interviewed ? themeAccent : '#4a5568'
                              }}
                            >
                              <User className="w-8 h-8 text-slate-400" />
                            </div>
                          )}

                          {/* Interview indicator */}
                          {interviewed && (
                            <div
                              className="absolute -bottom-1 -right-1 rounded-full p-1"
                              style={{ backgroundColor: themeAccent }}
                            >
                              <User className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>

                        {/* Name */}
                        <p className="text-white font-bold mt-2 text-sm text-center truncate">
                          {suspect.name}
                        </p>
                        <p className="text-xs text-center truncate" style={{ color: themeAccent }}>
                          {suspect.occupation}
                        </p>

                        {/* Status */}
                        <p className={`text-xs text-center mt-1 ${interviewed ? 'text-green-400' : 'text-gray-500'}`}>
                          {interviewed ? 'Interviewed' : 'Not interviewed'}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Footer hint */}
          <div className="pt-2 border-t border-white/10">
            <p className="text-xs text-gray-400 text-center">
              Click on a suspect to view their details and interview history
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suspect Detail Modal */}
      {selectedSuspect && (
        <SuspectDetailModal
          suspect={selectedSuspect}
          open={!!selectedSuspect}
          onClose={() => setSelectedSuspect(null)}
          onInterview={isGameOver ? undefined : handleInterviewClick}
          interviewHistory={getInterviewHistory(selectedSuspect.id)}
          hasBeenInterviewed={hasBeenInterviewed(selectedSuspect.id)}
        />
      )}

      {/* Already Interrogated Popup */}
      <Dialog open={alreadyInterrogatedPopup} onOpenChange={setAlreadyInterrogatedPopup}>
        <DialogContent className="max-w-md bg-gradient-to-br from-slate-900 to-slate-800 border-2" style={{ borderColor: themeAccent }}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <AlertCircle className="w-6 h-6" style={{ color: themeAccent }} />
              Interrogation Complete
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-300 text-center">
              You have already completed all interrogation rounds with <span className="font-bold text-white">{popupSuspectName}</span>.
            </p>
            <p className="text-gray-400 text-sm text-center mt-3">
              Review their interview history in the Suspect Wall or Case File.
            </p>
          </div>
          <div className="flex justify-center">
            <Button
              onClick={() => setAlreadyInterrogatedPopup(false)}
              className="px-6 text-white font-semibold"
              style={{ backgroundColor: themeAccent }}
            >
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SuspectWall;
